import { createContext, useContext } from 'react';
import type {
  OasisValidationError,
  ShpUnsendableReason,
} from '../../services/api/types.gen';
import { fieldForCmsItem } from './cmsFixTarget';

/**
 * A CMS rule's description as the All CMS card and the OASIS Manager's error
 * dialog word it, for the line under the field it is fixed in.
 */
export const formatValidationDescription = (description: string): string => {
  return description
    .replace(/\[blank\]|\[\^\]/gi, 'blank')
    .replace(/\[Checked \(Yes\)\]/gi, 'Yes')
    .replace(/\bmust not equal\s+blank\b/gi, 'cannot be blank')
    .replace(/\bmust equal\s+Yes\b/gi, 'must be Yes')
    .replace(/\bcontrols\b/gi, 'requires')
    .replace(/\s+/g, ' ')
    .trim();
};

/** Why the nightly SHP batch does not send a chart, as the OASIS Manager says. */
export const SHP_UNSENDABLE_REASONS: Record<ShpUnsendableReason, string> = {
  not_cms_assessment: 'Not a CMS assessment',
  not_handed_in: 'Not handed in',
  missed: 'Missed visit',
  imported: 'Imported chart',
  outside_record: 'Completed with outside record',
  test_patient: 'Test patient',
  not_test_patient: 'SHP test sends test patients only',
  no_provider_number: 'Clinic has no SHP number',
  provider_number_not_numeric: 'SHP number must be digits only',
  provider_not_recognized: 'SHP does not recognise this SHP number',
  no_qies_agency_id: 'Clinic has no QIES ID',
};

/** Field id (or qid) to the text shown under it. */
export type CmsFieldErrors = Record<string, string>;

function collect(
  errors: OasisValidationError[],
  keysOf: (error: OasisValidationError) => (string | undefined)[]
): CmsFieldErrors {
  const byKey = new Map<string, string[]>();
  errors
    .filter(error => error.severity === 'fatal')
    .forEach(error => {
      const text = formatValidationDescription(error.description || '');
      if (!text) return;
      new Set(keysOf(error)).forEach(key => {
        if (!key) return;
        const texts = byKey.get(key) ?? [];
        if (!texts.includes(text)) byKey.set(key, [...texts, text]);
      });
    });
  return Object.fromEntries(
    [...byKey].map(([key, texts]) => [key, texts.join('; ')])
  );
}

/** Each Patient Info or clinic-settings input's CMS fatals, by input id. */
export function cmsErrorsByField(
  errors: OasisValidationError[]
): CmsFieldErrors {
  return collect(errors, error =>
    (error.oasis_fields ?? []).map(fieldForCmsItem)
  );
}

/** Each chart question's CMS fatals, under the question the error leads with. */
export function cmsErrorsByQid(errors: OasisValidationError[]): CmsFieldErrors {
  return collect(errors, error => [error.qids?.[0]]);
}

/** The chart's live CMS fatals by qid, for each question to show its own. */
export const CmsQuestionErrorsContext = createContext<CmsFieldErrors>({});

export function useCmsQuestionError(qid: string): string | undefined {
  return useContext(CmsQuestionErrorsContext)[qid];
}

/** The id of the line under `fieldId`, for its `aria-describedby`. */
export const cmsFieldErrorId = (fieldId: string) => `${fieldId}-cms-error`;
